import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Search, Package, Users, ArrowRight, Command, X, Layers, BarChart3, Settings } from 'lucide-react';

export default function CommandPalette({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ products: [], customers: [], pages: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();

  const handleSearch = useCallback(async (q) => {
    if (!q) {
      setResults({ products: [], customers: [], pages: [] });
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${q}`);
      setResults(res.data);
      setSelectedIndex(0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => handleSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, handleSearch]);

  const flattenedResults = [
    ...(Array.isArray(results.pages) ? results.pages : []).map(p => ({ ...p, type: 'page', icon: Layers })),
    ...(Array.isArray(results.products) ? results.products : []).map(p => ({ ...p, type: 'product', icon: Package, name: p.name, path: `/app/inventory` })),
    ...(Array.isArray(results.customers) ? results.customers : []).map(c => ({ ...c, type: 'customer', icon: Users, name: c.name, path: `/app/customers` }))
  ];

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      setSelectedIndex(prev => (prev + 1) % flattenedResults.length);
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex(prev => (prev - 1 + flattenedResults.length) % flattenedResults.length);
    } else if (e.key === 'Enter' && flattenedResults[selectedIndex]) {
      handleSelect(flattenedResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSelect = (item) => {
    navigate(item.path);
    onClose();
    setQuery('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[10vh] px-4">
      <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose}></div>
      
      <div className="bg-theme-card w-full max-w-2xl rounded-3xl shadow-2xl border border-theme overflow-hidden flex flex-col relative animate-palette-in">
        <div className="p-5 border-b border-theme flex items-center gap-4">
          <Search className="text-brand" size={24} />
          <input 
            autoFocus
            type="text" 
            placeholder="Search products, customers, or pages..." 
            className="flex-1 bg-transparent border-none outline-none text-theme-main text-lg font-bold placeholder:text-theme-muted"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="flex items-center gap-2 px-2 py-1 bg-theme-main rounded-lg border border-theme">
            <span className="text-[10px] font-black text-theme-muted uppercase">ESC to close</span>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2 custom-scrollbar">
          {flattenedResults.length > 0 ? (
            <div className="space-y-1">
              {flattenedResults.map((item, idx) => (
                <button
                  key={`${item.type}-${item.id || item.name}`}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all ${
                    idx === selectedIndex ? 'bg-brand text-white shadow-lg shadow-brand/20' : 'hover:bg-theme-main/10 text-theme-main'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-xl ${idx === selectedIndex ? 'bg-white/20' : 'bg-theme-main/20'}`}>
                      <item.icon size={20} />
                    </div>
                    <div className="text-left">
                      <div className="font-black text-sm uppercase tracking-tight">{item.name}</div>
                      <div className={`text-[10px] font-bold uppercase tracking-widest ${idx === selectedIndex ? 'text-white/70' : 'text-theme-muted'}`}>
                        {item.type} {item.sku ? `• SKU: ${item.sku}` : item.email ? `• ${item.email}` : ''}
                      </div>
                    </div>
                  </div>
                  <ArrowRight size={18} className={idx === selectedIndex ? 'opacity-100' : 'opacity-0'} />
                </button>
              ))}
            </div>
          ) : query.length > 1 ? (
            <div className="p-12 text-center text-theme-muted flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-theme-main/10 flex items-center justify-center mb-4">
                <Search size={32} className="opacity-20" />
              </div>
              <p className="font-black uppercase text-xs tracking-widest">No results found for "{query}"</p>
            </div>
          ) : (
            <div className="p-8 text-center text-theme-muted">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-6">Quick Shortcuts</p>
              <div className="grid grid-cols-2 gap-3">
                <Shortcut icon={Command} label="Global Search" keybind="Ctrl + K" />
                <Shortcut icon={BarChart3} label="Reports" keybind="Alt + R" />
                <Shortcut icon={Settings} label="Settings" keybind="Alt + S" />
                <Shortcut icon={X} label="Close Palette" keybind="Esc" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Shortcut({ icon: Icon, label, keybind }) {
  return (
    <div className="flex items-center justify-between p-3 bg-theme-main/5 rounded-xl border border-theme">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-brand" />
        <span className="text-[10px] font-black uppercase tracking-tight text-theme-main">{label}</span>
      </div>
      <span className="text-[9px] font-mono bg-theme-card px-1.5 py-0.5 rounded border border-theme text-theme-muted">{keybind}</span>
    </div>
  );
}
